import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from 'src/config/firebase';

/**
 * Updates a trainer's online status and last seen timestamp in Firestore.
 */
export const setTrainerOnlineStatus = async (uid: string, isOnline: boolean): Promise<void> => {
  if (!uid || uid.includes('@')) return;

  try {
    const trainerDocRef = doc(db, 'trainer_app_users', uid);
    await setDoc(
      trainerDocRef,
      {
        is_online: isOnline,
        last_seen: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (error: any) {
    if (error?.code === 'permission-denied') {
      // Ignore permission denied errors on logout where the token is already cleared
      return;
    }
    console.error(`[Trainer Presence] Failed to update status (isOnline=${isOnline}):`, error);
  }
};

/**
 * Initializes automatic presence tracking for the active trainer.
 * Handles heartbeats, tab visibility changes, and window unload.
 * Returns a cleanup function.
 */
export const initializeTrainerPresence = (uid: string): (() => void) => {
  if (!uid || uid.includes('@')) {
    return () => {};
  }

  // 1. Immediately mark trainer as online
  setTrainerOnlineStatus(uid, true);

  // 2. Periodic heartbeat every 60 seconds while active
  const HEARTBEAT_INTERVAL_MS = 60 * 1000;
  const heartbeatTimer = setInterval(() => {
    if (document.visibilityState === 'visible') {
      setTrainerOnlineStatus(uid, true);
    }
  }, HEARTBEAT_INTERVAL_MS);

  // 3. Tab visibility change handler
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      setTrainerOnlineStatus(uid, true);
    } else {
      // Refresh last_seen when backgrounded
      setTrainerOnlineStatus(uid, true);
    }
  };

  // 4. Page hide / unload handlers
  const handlePageHide = () => {
    setTrainerOnlineStatus(uid, false);
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('beforeunload', handlePageHide);

  // Cleanup function
  return () => {
    clearInterval(heartbeatTimer);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', handlePageHide);
    window.removeEventListener('beforeunload', handlePageHide);
    // Mark offline on unmount/cleanup
    setTrainerOnlineStatus(uid, false);
  };
};
